import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { dashboardApi, presupuestosApi } from '../lib/api';
import { formatCurrency, formatDate, getStatusColor, getStatusLabel } from '../lib/utils';
import { toast } from 'sonner';
import { 
  FileText, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  Plus,
  ArrowUpRight,
  Eye
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

export default function DashboardPage() {
  const { empresaActual } = useAuth();
  const navigate = useNavigate();
  const [metricas, setMetricas] = useState(null);
  const [presupuestosRecientes, setPresupuestosRecientes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      if (!empresaActual?.id) return;
      
      try {
        const [metricasRes, presupuestosRes] = await Promise.all([
          dashboardApi.getMetricas(empresaActual.id),
          presupuestosApi.getAll(empresaActual.id)
        ]);
        setMetricas(metricasRes.data);
        setPresupuestosRecientes(presupuestosRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error loading dashboard:', error);
        toast.error('Error al cargar el dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [empresaActual?.id]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  const metricCards = [
    {
      title: 'Enviados este mes',
      value: metricas?.total_enviados || 0,
      icon: FileText,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Valor cotizado',
      value: formatCurrency(metricas?.valor_total || 0),
      icon: TrendingUp,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100'
    },
    {
      title: 'Tasa de aprobación',
      value: `${metricas?.tasa_aprobacion || 0}%`,
      icon: CheckCircle,
      color: 'text-violet-600',
      bgColor: 'bg-violet-100'
    },
    {
      title: 'Pendientes',
      value: metricas?.pendientes || 0,
      icon: Clock,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100'
    }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-fadeIn" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Dashboard
            </h1>
            <p className="text-slate-500 mt-1">
              Bienvenido de vuelta. Aquí tienes un resumen de tu actividad.
            </p>
          </div>
          <Button 
            className="bg-blue-600 hover:bg-blue-700"
            onClick={() => navigate('/presupuestos/nuevo')}
            data-testid="dashboard-new-quote-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Presupuesto
          </Button>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {metricCards.map((metric, index) => (
            <Card key={index} className="metric-card" data-testid={`metric-${metric.title.toLowerCase().replace(/\s/g, '-')}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{metric.title}</p>
                    <p className="metric-value mt-1">{metric.value}</p>
                  </div>
                  <div className={`w-10 h-10 ${metric.bgColor} rounded-lg flex items-center justify-center`}>
                    <metric.icon className={`w-5 h-5 ${metric.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Secondary Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {metricas?.aprobados_semana || 0}
                </p>
                <p className="text-sm text-slate-500">Aprobados esta semana</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {metricas?.por_vencer || 0}
                </p>
                <p className="text-sm text-slate-500">Por vencer (próximos 7 días)</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Chart and Recent */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chart */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Actividad de los últimos 6 meses</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={metricas?.actividad || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="mes" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                      }}
                    />
                    <Legend />
                    <Bar dataKey="enviados" name="Enviados" fill="#2563eb" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="aprobados" name="Aprobados" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Recent Quotes */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Presupuestos Recientes</CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-blue-600"
                onClick={() => navigate('/presupuestos')}
              >
                Ver todos
                <ArrowUpRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {presupuestosRecientes.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                  <p>No hay presupuestos aún</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {presupuestosRecientes.map((p) => (
                    <div 
                      key={p.id} 
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/presupuestos/${p.id}`)}
                      data-testid={`recent-quote-${p.numero}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{p.numero}</p>
                        <p className="text-sm text-slate-500 truncate">{p.cliente?.nombre || 'Sin cliente'}</p>
                      </div>
                      <div className="text-right ml-4">
                        <p className="font-medium text-slate-900">{formatCurrency(p.total)}</p>
                        <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded border ${getStatusColor(p.estado)}`}>
                          {getStatusLabel(p.estado)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
